import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ChevronRight, ArrowLeft, TrendingDown, Clock, AlertTriangle, Shield, Zap, CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";

interface StakesStepProps {
  optionALabel: string;
  optionBLabel: string;
  initialWrongChoiceA: string;
  initialWrongChoiceB: string;
  initialReversibilityA: string;
  initialReversibilityB: string;
  initialInaction: string;
  initialWorstCase: string;
  initialWorstCaseReason: string;
  initialWorstCaseTrigger?: string;
  initialWorstCaseProbability?: string;
  initialTimeframeA: string;
  initialTimeframeB: string;
  initialInactionTimeframe: string;
  onNext: (data: StakesData) => void;
  onBack: () => void;
}

export interface StakesData {
  wrongChoiceA: string;
  wrongChoiceB: string;
  reversibilityA: string;
  reversibilityB: string;
  timeframeA: string;
  timeframeB: string;
  inaction: string;
  inactionTimeframe: string;
  worstCase: string;
  worstCaseReason: string;
  worstCaseTrigger: string;
  worstCaseProbability: string;
}

const TIMEFRAME_OPTIONS = [
  { value: "0-3", label: "0–3 Mon." },
  { value: "3-6", label: "3–6 Mon." },
  { value: "6-12", label: "6–12 Mon." },
  { value: "12+", label: ">12 Mon." },
];

const REVERSIBILITY_OPTIONS = [
  { value: "schnell", label: "Schnell (<1 Mon.)" },
  { value: "mittel", label: "Mittel (1–6 Mon.)" },
  { value: "schwer", label: "Schwer (>6 Mon.)" },
  { value: "irreversibel", label: "Irreversibel" },
];

const PROBABILITY_OPTIONS = [
  { value: "niedrig", label: "Niedrig" },
  { value: "mittel", label: "Mittel" },
  { value: "hoch", label: "Hoch" },
];

const INACTION_PROMPTS = [
  "Welche Opportunitäten verlieren wir konkret?",
  "Welcher Wettbewerber zieht vorbei?",
  "Welches Momentum verpufft?",
];

const KPI_REGEX = /\d+|€|\$|wochen|monate|tage|quartal|churn|pipeline|cac|arr|mrr|nps|umsatz|kosten|marge|budget|prozent|%|jahr|halbjahr/i;

const REV_WEIGHT: Record<string, number> = { schnell: 1, mittel: 2, schwer: 3, irreversibel: 4 };
const TF_WEIGHT: Record<string, number> = { "0-3": 4, "3-6": 3, "6-12": 2, "12+": 1 };

function computeOptionRisk(text: string, tf: string, rev: string): number {
  let score = 0;
  if (text.trim().length > 20) score += 1;
  if (KPI_REGEX.test(text)) score += 2;
  score += TF_WEIGHT[tf] || 0;
  score += REV_WEIGHT[rev] || 0;
  return Math.min(score, 10);
}

function computeRiskLevel(a: string, b: string, inaction: string, worstCase: string, tfA: string, tfB: string, revA: string, revB: string): { level: number; label: string; color: string } {
  let score = 0;
  if (a.trim().length > 20) score += 1;
  if (b.trim().length > 20) score += 1;
  if (inaction.trim().length > 15) score += 1;
  if (worstCase.trim().length > 10) score += 1;
  if (KPI_REGEX.test(a)) score += 1;
  if (KPI_REGEX.test(b)) score += 1;
  if (KPI_REGEX.test(inaction)) score += 1;
  if (tfA === "0-3" || tfB === "0-3") score += 2;
  else if (tfA === "3-6" || tfB === "3-6") score += 1;
  if (revA === "schwer" || revB === "schwer" || revA === "irreversibel" || revB === "irreversibel") score += 1;

  if (score <= 2) return { level: 1, label: "Niedriges Risiko", color: "text-muted-foreground" };
  if (score <= 5) return { level: 2, label: "Mittleres Risiko", color: "text-primary" };
  return { level: 3, label: "Strategisch kritisch", color: "text-destructive" };
}

function riskColor(score: number): string {
  if (score <= 3) return "bg-muted-foreground/30";
  if (score <= 6) return "bg-primary/60";
  return "bg-destructive";
}

function riskLabel(score: number): string {
  if (score <= 3) return "Gering";
  if (score <= 6) return "Mittel";
  return "Hoch";
}

/* Segment button group */
const SegmentGroup = ({
  options,
  value,
  onChange,
  label,
}: {
  options: { value: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
  label: string;
}) => (
  <div className="space-y-1.5">
    {label && <Label className="text-xs text-muted-foreground">{label}</Label>}
    <div className="flex flex-wrap gap-1.5">
      {options.map((opt) => (
        <button
          key={opt.value}
          type="button"
          onClick={() => onChange(opt.value)}
          className={cn(
            "px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200",
            value === opt.value
              ? "border-foreground bg-foreground text-background shadow-sm font-semibold"
              : "border-border bg-muted/60 text-muted-foreground hover:bg-accent hover:text-accent-foreground hover:border-border"
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  </div>
);

/* Styled input classes for high-contrast inputs */
const inputClasses = "min-h-[52px] text-[15px] leading-relaxed rounded-xl bg-background border border-border placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary transition-colors";
const inputClassesSm = "min-h-[44px] text-sm rounded-xl bg-background border border-border placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:border-primary transition-colors";
const inputClassesWorstCase = "min-h-[44px] text-sm rounded-xl bg-background border border-destructive/25 placeholder:text-muted-foreground/60 focus-visible:ring-2 focus-visible:ring-destructive/20 focus-visible:border-destructive/40 transition-colors";

export const StakesStep = ({
  optionALabel,
  optionBLabel,
  initialWrongChoiceA,
  initialWrongChoiceB,
  initialReversibilityA,
  initialReversibilityB,
  initialInaction,
  initialWorstCase,
  initialWorstCaseReason,
  initialWorstCaseTrigger = "",
  initialWorstCaseProbability = "",
  initialTimeframeA,
  initialTimeframeB,
  initialInactionTimeframe,
  onNext,
  onBack,
}: StakesStepProps) => {
  const [wrongChoiceA, setWrongChoiceA] = useState(initialWrongChoiceA);
  const [wrongChoiceB, setWrongChoiceB] = useState(initialWrongChoiceB);
  const [reversibilityA, setReversibilityA] = useState(initialReversibilityA);
  const [reversibilityB, setReversibilityB] = useState(initialReversibilityB);
  const [timeframeA, setTimeframeA] = useState(initialTimeframeA);
  const [timeframeB, setTimeframeB] = useState(initialTimeframeB);
  const [inaction, setInaction] = useState(initialInaction);
  const [inactionTimeframe, setInactionTimeframe] = useState(initialInactionTimeframe);
  const [worstCase, setWorstCase] = useState(initialWorstCase);
  const [worstCaseReason, setWorstCaseReason] = useState(initialWorstCaseReason);
  const [worstCaseTrigger, setWorstCaseTrigger] = useState(initialWorstCaseTrigger);
  const [worstCaseProbability, setWorstCaseProbability] = useState(initialWorstCaseProbability);
  const [focusedBlock, setFocusedBlock] = useState<string | null>(null);

  const isValid = wrongChoiceA.trim().length >= 10 && wrongChoiceB.trim().length >= 10 && timeframeA.length > 0 && timeframeB.length > 0 && reversibilityA.length > 0 && reversibilityB.length > 0 && worstCase.trim().length >= 10 && worstCaseTrigger.trim().length >= 5;

  const hasMinInput = wrongChoiceA.trim().length >= 10 || wrongChoiceB.trim().length >= 10;

  const riskScoreA = useMemo(() => computeOptionRisk(wrongChoiceA, timeframeA, reversibilityA), [wrongChoiceA, timeframeA, reversibilityA]);
  const riskScoreB = useMemo(() => computeOptionRisk(wrongChoiceB, timeframeB, reversibilityB), [wrongChoiceB, timeframeB, reversibilityB]);

  const worstCaseContext = useMemo(() => {
    if (!timeframeA && !timeframeB) return null;
    const tfToMonths: Record<string, number> = { "0-3": 3, "3-6": 6, "6-12": 12, "12+": 18 };
    const shorter = Math.min(tfToMonths[timeframeA] || 99, tfToMonths[timeframeB] || 99);
    const harder = [reversibilityA, reversibilityB].some(r => r === "schwer" || r === "irreversibel");
    if (shorter <= 6 && harder) return "Kurzer Zeitraum + schwere Reversibilität → hohes strategisches Risiko.";
    if (shorter <= 6) return `Auswirkungen schon innerhalb von ${shorter} Monaten sichtbar.`;
    if (harder) return "Schwere Reversibilität erhöht die Tragweite erheblich.";
    return null;
  }, [timeframeA, timeframeB, reversibilityA, reversibilityB]);

  const risk = useMemo(
    () => computeRiskLevel(wrongChoiceA, wrongChoiceB, inaction, worstCase, timeframeA, timeframeB, reversibilityA, reversibilityB),
    [wrongChoiceA, wrongChoiceB, inaction, worstCase, timeframeA, timeframeB, reversibilityA, reversibilityB]
  );

  const handleSubmit = () => {
    onNext({ wrongChoiceA, wrongChoiceB, reversibilityA, reversibilityB, timeframeA, timeframeB, inaction, inactionTimeframe, worstCase, worstCaseReason, worstCaseTrigger, worstCaseProbability });
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter" && isValid) handleSubmit();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isValid, wrongChoiceA, wrongChoiceB, reversibilityA, reversibilityB, timeframeA, timeframeB, inaction, inactionTimeframe, worstCase, worstCaseReason, worstCaseTrigger, worstCaseProbability]);

  const labelA = optionALabel || "Option A";
  const labelB = optionBLabel || "Option B";

  const renderOptionBlock = (
    label: string,
    id: string,
    value: string,
    setValue: (v: string) => void,
    timeframe: string,
    setTimeframe: (v: string) => void,
    reversibility: string,
    setReversibility: (v: string) => void,
    riskScore: number,
  ) => (
    <div
      className={cn(
        "rounded-2xl border pt-9 pb-6 px-6 transition-all duration-300 space-y-4 relative overflow-visible",
        focusedBlock === id
          ? "border-primary/60 bg-background shadow-[0_4px_24px_hsl(var(--primary)/0.08)]"
          : focusedBlock && focusedBlock !== id
            ? "border-border/40 bg-background opacity-80"
            : "border-border/80 bg-background hover:border-primary/30"
      )}
    >
      {/* Strategy badge — positioned outside overflow */}
      <div
        className={cn(
          "absolute -top-3.5 left-6 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-widest border z-10",
          id === "optA"
            ? "bg-background text-primary border-primary/30 shadow-sm"
            : "bg-background text-accent-foreground border-border shadow-sm"
        )}
      >
        {id === "optA" ? "Strategie A" : "Strategie B"}
      </div>

      {/* Risk indicator bar on the left — inset to avoid border-radius collision */}
      <div
        className={cn(
          "absolute left-0 top-4 bottom-4 w-1 rounded-full transition-all duration-500",
          riskScore > 0 ? riskColor(riskScore) : "bg-border/30"
        )}
      />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingDown className="h-4 w-4 text-destructive/70" />
          <Label className="text-sm font-semibold text-foreground leading-relaxed">
            Wenn {label} falsch ist …
          </Label>
        </div>
        {riskScore > 0 && (
          <span className={cn(
            "text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border transition-all duration-300",
            riskScore <= 3
              ? "text-muted-foreground border-border/40 bg-muted/20"
              : riskScore <= 6
                ? "text-primary border-primary/30 bg-primary/10"
                : "text-destructive border-destructive/30 bg-destructive/10"
          )}>
            Risiko: {riskLabel(riskScore)}
          </span>
        )}
      </div>

      <Input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onFocus={() => setFocusedBlock(id)}
        onBlur={() => setFocusedBlock(null)}
        placeholder="Messbare Auswirkung: z. B. 300k € Fehlinvestition, 9 Monate Verzögerung"
        className={inputClasses}
      />

      <div className="flex flex-wrap items-start gap-4">
        <SegmentGroup
          label="Sichtbar nach"
          options={TIMEFRAME_OPTIONS}
          value={timeframe}
          onChange={setTimeframe}
        />
        <SegmentGroup
          label="Reversibilität"
          options={REVERSIBILITY_OPTIONS}
          value={reversibility}
          onChange={setReversibility}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-in max-w-[1000px] mx-auto">
      {/* Header */}
      <div className="space-y-4">
        <div className="flex items-center gap-3 mb-2">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <span className="text-xs font-medium text-muted-foreground/60 uppercase tracking-wider">Schritt 2 von 4</span>
            <span className="text-xs text-muted-foreground/40 mx-1.5">·</span>
            <span className="text-xs font-medium text-foreground/70">Konsequenzen verstehen</span>
          </div>
        </div>
        <h2 className="text-[28px] md:text-[34px] font-semibold font-serif leading-[1.2] tracking-tight text-foreground max-w-[720px]">
          Was riskieren wir konkret –{" "}
          <span className="text-primary">in Zahlen und Zeit?</span>
        </h2>
        <p className="text-muted-foreground text-base max-w-[640px]">
          Welche messbaren Konsequenzen entstehen, wenn diese Richtung falsch ist?
        </p>
      </div>

      {/* Live Risk Indicator */}
      {hasMinInput && (
        <div className="flex items-center gap-3 p-3 rounded-xl border border-border/40 bg-card/30 animate-fade-in">
          <div className="flex gap-1">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className={cn(
                  "h-2 w-8 rounded-full transition-all duration-500",
                  i <= risk.level
                    ? risk.level === 1 ? "bg-muted-foreground/40" : risk.level === 2 ? "bg-primary/60" : "bg-destructive"
                    : "bg-muted/30"
                )}
              />
            ))}
          </div>
          <span className={cn("text-xs font-medium transition-colors duration-300", risk.color)}>
            {risk.label}
          </span>
        </div>
      )}

      {/* A/B Risk blocks — stable equal-width grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-7">
        {renderOptionBlock(labelA, "optA", wrongChoiceA, setWrongChoiceA, timeframeA, setTimeframeA, reversibilityA, setReversibilityA, riskScoreA)}
        {renderOptionBlock(labelB, "optB", wrongChoiceB, setWrongChoiceB, timeframeB, setTimeframeB, reversibilityB, setReversibilityB, riskScoreB)}
      </div>

      {/* Relative Risk Comparison */}
      {riskScoreA > 0 && riskScoreB > 0 && (
        <div className="rounded-2xl border border-border/40 bg-card/30 p-4 space-y-3 animate-fade-in">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Risikovergleich</span>
          
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-muted-foreground w-20 text-right shrink-0 truncate">{labelA}</span>
            <div className="flex-1 h-3 bg-muted/20 rounded-full overflow-hidden relative">
              <div
                className={cn("h-full rounded-full transition-all duration-700", riskColor(riskScoreA))}
                style={{ width: `${Math.max(riskScoreA * 10, 5)}%` }}
              />
            </div>
            <span className="text-xs font-bold w-6 text-right">{riskScoreA}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-muted-foreground w-20 text-right shrink-0 truncate">{labelB}</span>
            <div className="flex-1 h-3 bg-muted/20 rounded-full overflow-hidden relative">
              <div
                className={cn("h-full rounded-full transition-all duration-700", riskColor(riskScoreB))}
                style={{ width: `${Math.max(riskScoreB * 10, 5)}%` }}
              />
            </div>
            <span className="text-xs font-bold w-6 text-right">{riskScoreB}</span>
          </div>

          {Math.abs(riskScoreA - riskScoreB) >= 2 && (
            <p className="text-xs text-primary font-medium pt-1 border-t border-border/20">
              ⚡ {riskScoreA > riskScoreB
                ? `${labelA} ist die größere strategische Wette.`
                : `${labelB} ist die größere strategische Wette.`
              }
            </p>
          )}
          {Math.abs(riskScoreA - riskScoreB) < 2 && (
            <p className="text-xs text-muted-foreground pt-1 border-t border-border/20">
              Beide Optionen tragen ähnliches Risiko – die Entscheidung liegt im Timing und der Reversibilität.
            </p>
          )}
        </div>
      )}

      {/* Block 2: Inaction — dashed border, distinct background */}
      <div
        className={cn(
          "rounded-2xl border-2 border-dashed p-5 transition-all duration-300 space-y-4 bg-muted/20",
          focusedBlock === "inaction"
            ? "border-primary/60 bg-primary/[0.03] shadow-[0_0_20px_hsl(var(--primary)/0.08)]"
            : "border-border/60"
        )}
      >
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4 text-primary/70" />
          <Label className="text-sm font-semibold text-foreground">
            Wenn wir gar nicht entscheiden …
          </Label>
        </div>

        <div className="flex flex-wrap gap-1.5 mb-1">
          {INACTION_PROMPTS.map((prompt) => (
            <button
              key={prompt}
              type="button"
              onClick={() => {
                if (!inaction.includes(prompt.replace("?", ""))) {
                  setInaction((prev) => prev ? `${prev} ${prompt}` : prompt);
                }
              }}
              className="text-[10px] px-2 py-1 rounded-full border border-border text-muted-foreground hover:border-primary/40 hover:text-primary hover:bg-accent transition-colors"
            >
              <Zap className="h-2.5 w-2.5 inline mr-1" />
              {prompt}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Input
            value={inaction}
            onChange={(e) => setInaction(e.target.value)}
            onFocus={() => setFocusedBlock("inaction")}
            onBlur={() => setFocusedBlock(null)}
            placeholder="Opportunitäten, Momentum, Wettbewerbsvorteil – was geht verloren?"
            className={cn(inputClasses, "flex-1")}
          />
          <div className="shrink-0">
            <SegmentGroup
              label=""
              options={TIMEFRAME_OPTIONS}
              value={inactionTimeframe}
              onChange={setInactionTimeframe}
            />
          </div>
        </div>
      </div>

      {/* Block 3: Worst Case — red accent, distinct background */}
      <div
        className={cn(
          "rounded-2xl border p-5 transition-all duration-300 space-y-4 relative overflow-visible bg-background",
          focusedBlock === "worst"
            ? "border-destructive/50 shadow-[0_0_20px_hsl(var(--destructive)/0.08)]"
            : "border-border/60"
        )}
      >
        {/* Red accent bar — inset to avoid border-radius collision */}
        <div className="absolute left-0 top-4 bottom-4 w-1 rounded-full bg-destructive" />
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2">
            <Shield className="h-4 w-4 text-destructive/70 mt-0.5" />
            <div>
              <Label className="text-sm font-semibold text-foreground">
                Worst Case – realistisch
              </Label>
              {worstCaseContext && (
                <p className="text-xs text-destructive/70 mt-1">{worstCaseContext}</p>
              )}
            </div>
          </div>
          {riskScoreA > 0 && riskScoreB > 0 && (
            <span className="text-[10px] font-medium text-destructive/60 bg-destructive/5 border border-destructive/15 rounded-full px-2.5 py-0.5 whitespace-nowrap">
              bezogen auf {riskScoreA >= riskScoreB ? labelA : labelB}
            </span>
          )}
        </div>

        {/* Szenario */}
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">Szenario (1–2 Sätze)</Label>
          <Textarea
            value={worstCase}
            onChange={(e) => { if (e.target.value.length <= 200) setWorstCase(e.target.value); }}
            onFocus={() => setFocusedBlock("worst")}
            onBlur={() => setFocusedBlock(null)}
            placeholder="Was ist das schlimmste realistische Szenario?"
            className={cn(inputClassesWorstCase, "min-h-[56px] resize-none text-[15px]")}
            rows={2}
          />
          <div className="flex justify-end">
            <span className={cn(
              "text-xs",
              worstCase.length > 180 ? "text-destructive" : "text-muted-foreground/50"
            )}>
              {worstCase.length} / 200
            </span>
          </div>
        </div>

        {/* Eintrittsbedingungen */}
        <div className="space-y-1 border-t border-border/30 pt-3">
          <Label className="text-xs text-muted-foreground">Eintrittsbedingungen – was müsste passieren?</Label>
          <Input
            value={worstCaseTrigger}
            onChange={(e) => setWorstCaseTrigger(e.target.value)}
            onFocus={() => setFocusedBlock("worst")}
            onBlur={() => setFocusedBlock(null)}
            placeholder="z. B. Kein Champion-Kunde, kein Sales-Setup, Budget <500k €"
            className={inputClassesWorstCase}
          />
        </div>

        {/* Warum realistisch */}
        <div className="space-y-1 border-t border-border/30 pt-3">
          <Label className="text-xs text-muted-foreground">Warum ist das realistisch? (1 Satz)</Label>
          <Input
            value={worstCaseReason}
            onChange={(e) => setWorstCaseReason(e.target.value)}
            onFocus={() => setFocusedBlock("worst")}
            onBlur={() => setFocusedBlock(null)}
            placeholder="z. B. Sales-Zyklen dauern 9–18 Monate, Referenzen fehlen"
            className={inputClassesWorstCase}
          />
        </div>

        {/* Wahrscheinlichkeit */}
        <div className="border-t border-border/30 pt-3">
          <div className="flex items-center gap-3">
            <Label className="text-xs text-muted-foreground whitespace-nowrap">Eintrittswahrscheinlichkeit:</Label>
            <div className="flex gap-1.5">
              {PROBABILITY_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setWorstCaseProbability(opt.value)}
                  className={cn(
                    "px-3 py-1.5 text-xs font-medium rounded-lg border transition-all duration-200",
                    worstCaseProbability === opt.value
                      ? opt.value === "hoch"
                        ? "border-destructive bg-destructive text-destructive-foreground shadow-sm"
                        : opt.value === "mittel"
                          ? "border-primary bg-primary text-primary-foreground shadow-sm"
                          : "border-muted-foreground/40 bg-muted text-foreground shadow-sm"
                      : "border-border bg-muted/60 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Strategic banner */}
      {risk.level === 3 && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20 animate-fade-in">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
          <p className="text-sm text-destructive font-medium">
            Diese Entscheidung hat strategische Tragweite.
          </p>
        </div>
      )}

      {/* Completion hint */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        {isValid ? (
          <span className="flex items-center gap-1.5 text-primary font-medium animate-fade-in">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Analyse vollständig – du kannst fortfahren
          </span>
        ) : (
          <span>Beide Optionen + Zeitraum + Reversibilität + Worst Case ausfüllen</span>
        )}
        <span className="opacity-60">⌘ + Enter</span>
      </div>

      {/* Footer buttons */}
      <div className="flex gap-3">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2 h-12 px-6 rounded-xl border-border hover:bg-accent hover:border-border"
        >
          <ArrowLeft className="h-4 w-4" />
          Zurück
        </Button>
        <Button
          onClick={handleSubmit}
          disabled={!isValid}
          className={cn(
            "flex-1 gap-2 h-12 rounded-xl transition-all",
            !isValid && "opacity-50 cursor-not-allowed"
          )}
        >
          Weiter
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
